import { useLayoutEffect, useMemo, useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { Download } from "lucide-react"
import AppLayout from "@/components/AppLayout"
import { Button } from "@/components/ui/Button"
import { Card } from "@/components/ui/Card"
import { Input } from "@/components/ui/Input"
import ActualsPaths from "@/pages/Plan/components/ActualsPaths"
import ActualsTable, { type ActualsRow, type RowKind } from "@/pages/Plan/components/ActualsTable"
import type { PeriodKey } from "@/pages/Plan/components/ActualsToolbar"
import type { PeriodValue } from "@/pages/Revenue/components/PeriodMenu"
import { formatDate, formatRange } from "@/pages/Revenue/format"
import { nextSort, type SortState } from "@/pages/Revenue/sort"
import { MARKET_ID, SCOPE_TREE } from "./data"
import { CATEGORY_TREE, landing, shareOf, tableRows } from "./Landing/landingData"
import MonthPace from "./Landing/MonthPace"
import PageFilters from "./Landing/PageFilters"
import SectionHeading from "./Landing/SectionHeading"
import TableControls from "./Landing/TableControls"
import YearLanding from "./Landing/YearLanding"

interface Props {
  // Overview's own Scope and Category.
  oscope: string | null
  ocategory: string | null
  // The table's Scope and Category.
  scope: string | null
  category: string | null
  rows: RowKind | null
}

interface Params {
  oscope: string
  ocategory: string
  scope: string
  category: string
  rows: RowKind
}

const CURRENCY = "VND"
const SEARCH_PLACEHOLDER: Record<RowKind, string> = {
  products: "Search products…",
  categories: "Search categories…",
  channels: "Search channels…",
}
const PATH = "/wireframes/plan/actuals-landing"

const TODAY = "2026-10-25"
const PLAN_BEGIN = "2026-01-01"
const PLAN_END = "2026-12-31"

// A preset's dates; custom keeps the picked ones.
function periodFor(preset: PeriodKey | "custom", from?: string, to?: string): PeriodValue<PeriodKey> {
  if (preset === "today") return { preset, from: TODAY, to: TODAY }
  if (preset === "this_month") return { preset, from: "2026-10-01", to: "2026-10-31" }
  if (preset === "plan_period") return { preset, from: PLAN_BEGIN, to: PLAN_END }
  return { preset, from: from ?? TODAY, to: to ?? TODAY }
}

const range = (from: string, to: string) => (from === to ? formatDate(from) : formatRange(from, to))

// Changing Scope, Category or Break down by reloads the page, which rebuilds the
// scroll pane at the top. Remember where the pane was and put it back.
let savedScroll: number | null = null

function useKeepScroll() {
  const ref = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const pane = ref.current?.parentElement
    if (!pane) return
    if (savedScroll != null) {
      const top = savedScroll
      savedScroll = null
      pane.scrollTop = top
      // Again after the charts have measured themselves.
      requestAnimationFrame(() => { pane.scrollTop = top })
    }
    return router.on("before", (event) => {
      if (event.detail.visit.url.pathname === PATH) savedScroll = pane.scrollTop
    })
  }, [])
  return ref
}

function sum(rows: ActualsRow[], key: "actual" | "plan") {
  return rows.reduce((s, r) => s + (r[key] ?? 0), 0)
}

// Plan vs Actual with "where we will land" on top: the full year (target,
// actual so far, latest estimate) and the current month's pace, then the
// existing table. Each section has its own set of controls: Overview has
// Scope and Category next to its heading; the table has Scope, Category,
// Period, Break down by, search and export inside its card.
export default function ActualsLandingWireframe({ oscope, ocategory, scope, category, rows }: Props) {
  const validScope = (id: string | null) => (SCOPE_TREE.some((n) => n.id === id) ? (id as string) : MARKET_ID)
  const validCategory = (id: string | null) => (CATEGORY_TREE.some((n) => n.id === id) ? (id as string) : "all")
  const overviewScopeId = validScope(oscope)
  const overviewCategoryId = validCategory(ocategory)
  const scopeId = validScope(scope)
  const categoryId = validCategory(category)
  const kind: RowKind = rows === "categories" || rows === "channels" ? rows : "products"
  const scopeNodeOf = (id: string) => SCOPE_TREE.find((n) => n.id === id)!
  const categoryNodeOf = (id: string) => (id === "all" ? null : CATEGORY_TREE.find((n) => n.id === id)!)
  const scopeNode = scopeNodeOf(scopeId)
  const categoryNode = categoryNodeOf(categoryId)
  const [query, setQuery] = useState("")
  const [sort, setSort] = useState<SortState>({ sort: null, dir: "desc" })
  const [period, setPeriod] = useState(() => periodFor("plan_period"))
  const rootRef = useKeepScroll()
  // Actuals stop at today; the plan covers the whole picked period.
  const actualTo = period.to < TODAY ? period.to : TODAY

  // Every link keeps both sections' choices and changes only what it names.
  const current: Params = { oscope: overviewScopeId, ocategory: overviewCategoryId, scope: scopeId, category: categoryId, rows: kind }
  const link = (patch: Partial<Params>) => `${PATH}?${new URLSearchParams({ ...current, ...patch }).toString()}`
  const href = (s = scopeId, k: RowKind = kind, c = categoryId) => link({ scope: s, rows: k, category: c })

  const figures = useMemo(
    () => landing(shareOf(overviewScopeId, overviewCategoryId)),
    [overviewScopeId, overviewCategoryId],
  )
  const all = useMemo(() => tableRows(scopeId, categoryId, kind), [scopeId, categoryId, kind])
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    const found = all.filter((r) => !q || [r.name, r.full_name, r.plan_node].some((v) => v?.toLowerCase().includes(q)))
    if (!sort.sort) return found.sort((a, b) => b.actual - a.actual)
    const key = sort.sort as "actual" | "gm" | "units"
    const dir = sort.dir === "asc" ? 1 : -1
    return found.sort((a, b) => dir * ((a[key] ?? 0) - (b[key] ?? 0)))
  }, [all, query, sort])
  const total = { actual: sum(all, "actual"), plan: sum(all, "plan"), planned_actual: sum(all, "actual"), unplanned: [], gm: 0.63 }

  return (
    <AppLayout
      header={
        <div className="flex items-center gap-6">
          <h1 className="m-0 text-base font-bold tracking-tight text-foreground">Plan vs Actual</h1>
        </div>
      }
    >
      <div ref={rootRef} className="space-y-4">
        <SectionHeading title="Overview">
          <PageFilters
            scopeTree={SCOPE_TREE}
            scope={scopeNodeOf(overviewScopeId)}
            hrefForScope={(s) => link({ oscope: s })}
            categoryTree={CATEGORY_TREE}
            category={categoryNodeOf(overviewCategoryId)}
            hrefForCategory={(c) => link({ ocategory: c })}
          />
        </SectionHeading>
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <YearLanding currency={CURRENCY} {...figures.year} />
          <MonthPace currency={CURRENCY} {...figures.month} />
        </div>

        {/* A full-width rule and extra space mark where Overview ends. */}
        <div className="!mt-8 border-t pt-4">
          <SectionHeading title="Comparison & Breakdown">
            <TableControls
              scopeTree={SCOPE_TREE}
              scope={scopeNode}
              hrefForScope={(s) => href(s)}
              categoryTree={CATEGORY_TREE}
              category={categoryNode}
              hrefForCategory={(c) => href(scopeId, kind, c)}
              period={period}
              planBegin={PLAN_BEGIN}
              planEnd={PLAN_END}
              onPeriodChange={(p) => setPeriod((cur) => periodFor(p.preset, p.from ?? cur.from, p.to ?? cur.to))}
              rowKind={kind}
              hrefForRowKind={(k) => href(scopeId, k)}
            />
          </SectionHeading>
        </div>
        <Card className="overflow-hidden">
          <ActualsPaths
            scopeTree={SCOPE_TREE}
            scopeId={scopeId}
            marketId={MARKET_ID}
            hrefForScope={(s) => href(s)}
            categoryTree={CATEGORY_TREE}
            categoryId={categoryNode?.id ?? null}
            hrefForCategory={(c) => href(scopeId, kind, c)}
          />
          <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3 text-sm">
            <div>
              Actual: <span className="font-medium">{range(period.from, actualTo)}</span>
              <span className="mx-2 text-muted-foreground">·</span>
              Plan: <span className="font-medium">{range(period.from, period.to)}</span>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={SEARCH_PLACEHOLDER[kind]}
                className="h-9 w-56"
              />
              <Button asChild variant="outline" size="sm" className="h-9">
                <a href="#">
                  <Download className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
                  Export Sales CSV
                </a>
              </Button>
            </div>
          </div>
          <ActualsTable
            rowKind={kind}
            rows={visible}
            total={total}
            currency={CURRENCY}
            showGm
            sort={sort}
            onSort={(key) => setSort((s) => nextSort(s, key))}
            linkForChannel={() => null}
            emptyText={query ? `Nothing matches “${query}”.` : "No products are planned here."}
            hrefForRow={(r) => {
              if (kind === "channels" && r.slug && r.id !== scopeId) return href(r.slug)
              if (kind === "categories" && r.slug && r.id !== categoryId) return href(scopeId, kind, r.slug)
              return null
            }}
          />
        </Card>
      </div>
    </AppLayout>
  )
}
